import { ColumnDef } from '@tanstack/react-table';
import { linkOptions, useSearch } from '@tanstack/react-router';
import { useEngagementPermissions, useUserRoles } from '@anytag/auth';
import {
  WarningReason,
  EngagementPost,
  EngagementDetail,
  SocialAccountStatus,
  CampaignSocialMediaType,
  EngagementCampaignStatus,
  EngagementCampaignSocialAccountPostStatus,
  useEngagementPostsMissingMetricsForReportQuery
} from '@anytag/graphql';
import { Anchor, CheckBox, TableCell, SocialIcon, MediaPreview, SocialIconProps } from '@anytag/shared-atoms';
import {
  useQueryHelper,
  useSelectItems,
  usePostDetailsParam,
  useGenerateInfluencerProfileLink
} from '@anytag/shared-hooks';
import { EngagementPostStatus, InfluencerAvatar, SpreadButton } from '@anytag/shared-molecules';
import { checkIsReelPost, cn } from '@anytag/shared-utils';
import { getDottedLineMessage, isStatusChangeable } from '../utils';
import { EngagementPostsSearchSchemaType } from '../../EngagementPostsFilter';

interface Props {
  engagement: EngagementDetail;
  posts: readonly EngagementPost[];
}

export const useEngagementPostsListTable = ({
  posts,
  engagement: { id, status: engagementStatus, isTtcmCampaign }
}: Props) => {
  const eggId = id.toString();
  const search = useSearch({ from: '/_private-routes/engagement/$id/posts' }) as EngagementPostsSearchSchemaType;
  const { setPostDetailsParam } = usePostDetailsParam();
  const { t, navigate } = useQueryHelper();
  const { isAdminStaffTalentAgencyPartner } = useUserRoles();
  const { generateProfileLink } = useGenerateInfluencerProfileLink();
  const { hideEditPostBtn, hideApproveRejectInappropriatePostBtn } = useEngagementPermissions();
  const { data } = useEngagementPostsMissingMetricsForReportQuery({
    variables: { campaignId: id }
  });

  const checkHasPostStatusControl = (post: Pick<EngagementPost, 'id' | 'status'>) =>
    !hideApproveRejectInappropriatePostBtn && !!post.id && !!post.status && isStatusChangeable(post.status);

  const postsActions = useSelectItems<EngagementPost, keyof EngagementPost>({
    key: 'id',
    listItems: posts.reduce<EngagementPost[]>(
      (acc, curr) => (checkHasPostStatusControl(curr) ? [...acc, curr] : acc),
      []
    )
  });

  const { allItemIds, selectItem, selectedItemIds, toggleSelectAllItems } = postsActions;
  const joinedStatuses = [
    EngagementCampaignSocialAccountPostStatus.JOINED,
    EngagementCampaignSocialAccountPostStatus.JOINED_WITH_PENDING_ORDER,
    EngagementCampaignSocialAccountPostStatus.JOINED_WITH_PENDING_INVITATION
  ];
  const isCampaignApproved = [EngagementCampaignStatus.ONGOING, EngagementCampaignStatus.FINISHED].includes(
    engagementStatus
  );
  const engagementPostsMissingMetricsIds = data?.engagementPostsMissingMetricsForReport?.ids;
  const columns: ColumnDef<EngagementPost>[] = [
    {
      size: 40,
      accessorKey: 'action',
      meta: { hidden: hideApproveRejectInappropriatePostBtn },
      header: () => <CheckBox onClick={toggleSelectAllItems} disabled={!allItemIds.length} />,
      cell: ({ row }) => {
        const { id, status } = row.original;
        const isSelectedPost = !!id && selectedItemIds.includes(id);

        return checkHasPostStatusControl({ id, status }) ? (
          <CheckBox
            checked={isSelectedPost}
            onClick={id ? () => selectItem(id) : undefined}
            help={t(isSelectedPost ? 'Unselect' : 'Select')}
          />
        ) : (
          <div className="w-4" />
        );
      }
    },
    {
      accessorKey: 'influencer',
      header: t<string>('HeaderColumn.Influencer'),
      cell: ({ row }) => {
        const { influencer } = row.original;

        return (
          <TableCell.Link
            label={influencer.name}
            className="flex w-50 items-center"
            {...generateProfileLink(influencer.id)}
          >
            <InfluencerAvatar size="40px" src={influencer.avatar} asChild />
            <TableCell.Text className="ml-2 min-w-0" value={influencer.name} />
          </TableCell.Link>
        );
      }
    },
    {
      size: 500, // take all the space
      accessorKey: 'content',
      header: t<string>('HeaderColumn.Content'),
      cell: ({ row }) => {
        const {
          id,
          status,
          content,
          postUrl,
          thumbNail,
          influencer,
          socialMedia,
          warningReason,
          socialAccountId,
          socialAccountStatus
        } = row.original;
        const engagementAddPostUrl = linkOptions({
          to: '/engagement/$id/posts/add',
          params: { id: eggId },
          search: {
            ...search,
            filter: {
              ...search.filter,
              id: String(influencer.id),
              socialAccountMedia: socialMedia,
              socialAccountId: String(socialAccountId || '')
            }
          }
        });
        const handlePostClick = () => (id ? setPostDetailsParam(id) : navigate(engagementAddPostUrl));

        const isJoinedStatuses = status && joinedStatuses.includes(status);
        const isRejectStatus = status === EngagementCampaignSocialAccountPostStatus.REJECT;
        const isReviewingStatus = status === EngagementCampaignSocialAccountPostStatus.REVIEWING;
        const rejectedByTikTok = isRejectStatus && warningReason === WarningReason.API_ERROR;
        const hasAddPostDottedPreview = status === EngagementCampaignSocialAccountPostStatus.JOINED;
        const isWaitingForRepost = isTtcmCampaign && isRejectStatus; // means rejected by Admin|Staff
        const isTikTokPostFromTtcm = isTtcmCampaign && socialMedia === CampaignSocialMediaType.TIKTOK;
        const disabledPostInteractions =
          status &&
          [
            EngagementCampaignSocialAccountPostStatus.JOINED_WITH_PENDING_INVITATION,
            EngagementCampaignSocialAccountPostStatus.JOINED_WITH_PENDING_ORDER
          ].includes(status);
        const needsAdminStaffReview = isTikTokPostFromTtcm && isReviewingStatus;
        const hasDottedLine = isTikTokPostFromTtcm
          ? (status &&
              [
                EngagementCampaignSocialAccountPostStatus.WARNING,
                EngagementCampaignSocialAccountPostStatus.JOINED,
                EngagementCampaignSocialAccountPostStatus.TTCM_REVIEWING,
                EngagementCampaignSocialAccountPostStatus.JOINED_WITH_PENDING_ORDER,
                EngagementCampaignSocialAccountPostStatus.JOINED_WITH_PENDING_INVITATION
              ].includes(status)) ||
            rejectedByTikTok
          : false;
        const hasMissingMetrics = id && engagementPostsMissingMetricsIds?.includes(id);

        return (
          <div className="flex items-center justify-between">
            {(hasDottedLine || hasAddPostDottedPreview) && !(isTtcmCampaign && isCampaignApproved) ? (
              <div
                onClick={!disabledPostInteractions ? handlePostClick : undefined}
                className={cn(
                  'flex items-center gap-2',
                  !disabledPostInteractions && 'cursor-pointer hover:text-active'
                )}
              >
                <div className="h-10 w-15 min-w-15 rounded-sm border-base-black-30 border-dashed bg-white" />
                {hasAddPostDottedPreview ? (
                  <Anchor variant="blue" {...engagementAddPostUrl} label={`+ ${t('Button.Add Post')}`} />
                ) : (
                  <div>{getDottedLineMessage({ t, status, postContent: content || '' })}</div>
                )}
              </div>
            ) : (
              <div onClick={handlePostClick} className="flex cursor-pointer items-center gap-2">
                <MediaPreview src={thumbNail} className="h-10 w-15" isReel={checkIsReelPost(postUrl)} />
                <Anchor
                  lines={2}
                  variant={isReviewingStatus || needsAdminStaffReview ? 'blue' : 'default'}
                  label={
                    isWaitingForRepost
                      ? t<string>('Waiting for re-posting')
                      : isReviewingStatus
                        ? t<string>('Review post')
                        : needsAdminStaffReview
                          ? t('Review video')
                          : content || '-'
                  }
                />
              </div>
            )}

            {(!isTikTokPostFromTtcm && !isJoinedStatuses) ||
            (isTikTokPostFromTtcm &&
              !!id &&
              ((!thumbNail && !content) || socialAccountStatus === SocialAccountStatus.SCRAPING)) ? (
              <SpreadButton
                className="ml-4"
                moreButtonProps={{ disabled: hideEditPostBtn && !hasAddPostDottedPreview }}
                options={[
                  {
                    icon: 'edit',
                    className: cn('relative', {
                      'after:absolute after:-top-[3px] after:-right-[3px] after:size-1.5 after:rounded-full after:bg-attention-red':
                        hasMissingMetrics
                    }),
                    tooltip: { help: t('Button.Edit') },
                    disabled: !isAdminStaffTalentAgencyPartner || !id,
                    hidden: hideEditPostBtn && !hasAddPostDottedPreview,
                    to: '/engagement/$id/posts/$postId',
                    params: { id: eggId, postId: id?.toString() },
                    search
                  }
                ]}
              />
            ) : null}
          </div>
        );
      }
    },
    {
      accessorKey: 'socialMedia',
      meta: { align: 'center' },
      header: t<string>('HeaderColumn.Social'),
      cell: ({ row }) => (
        <div className="mx-auto block w-max">
          <SocialIcon type={row.original.socialMedia as SocialIconProps['type']} />
        </div>
      )
    },
    {
      accessorKey: 'status',
      header: t<string>('HeaderColumn.Post Status'),
      cell: ({ row }) =>
        row.original.status ? (
          <EngagementPostStatus status={row.original.status} warningReason={row.original.warningReason || undefined} />
        ) : (
          ''
        )
    },
    {
      accessorKey: 'postedDate',
      header: t<string>('HeaderColumn.Post date'),
      cell: ({ row }) => <TableCell.Date className="min-w-20" value={row.original.postedDate} />
    }
  ];

  return { columns, postsActions };
};
